import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import m from 'mithril';
import { myClassTag, childTagsOf, stripClassPrefix, validSlug, tagUrl, diag } from '../utils/tagMatch';
import { loadTags, getTags } from '../utils/tagsSource';
import { markLandingSeen } from '../utils/landingState';
import { canReadContent } from '../utils/guestGuard';
import navigateTo from '../utils/navigate';

/**
 * 登录后，在页面底部固定显示「当前用户所属班级」的次标签导航（即页脚导航）。
 *
 * 判定逻辑（不写死班级数量，随后台配置动态推断）：
 *  - 当前用户「能发帖的主标签」即其所属班级（详见 utils/tagMatch.js 的 myClassTag）。
 *  - 该班级主标签的次标签 = 在线课堂 / 作业专区 / 经验交流 / 班干会议。
 *
 * 数据来自 utils/tagsSource.js（自行请求 /api/tags），不依赖 app.store。
 *
 * 未登录 / 不属于任何班级（如纯游客、未分班用户）→ 不渲染。
 */

// ?nav=debug 时，即使解析失败也渲染一条诊断信息，方便在手机上直接看原因
function debugNav() {
  try {
    return new URLSearchParams(window.location.search).get('nav') === 'debug';
  } catch (e) {
    return false;
  }
}

export default class ClassFooterNav extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    /*
     * 关键：本组件依赖标签数据，但首屏 API 载荷里不一定带 tags。
     * 之前没有主动拉取 → 标签列表为空 → 直接 return null，
     * 底部导航一个节点都不渲染（表现就是「固定栏不见了」）。
     */
    loadTags().then(
      () => m.redraw(),
      () => {}
    );
  }

  /*
   * 点击导航项：跳转，并标记着陆页已看过。
   *
   * navigateTo 内部先走 SPA（m.route.set），300ms 后若地址没变则用
   * 原生 location.href 兜底 —— 因为本组件是独立 m.mount 到 body 的根节点，
   * 不在 Flarum 路由组件树内，单纯 m.route.set 有时驱动不了主视图更新。
   */
  go(e, url) {
    e.preventDefault();
    markLandingSeen();
    navigateTo(url);
  }

  view() {
    /*
     * 必须防御：本组件是在 extension initializer 里同步 m.mount 的，
     * 那一刻 app.boot() 还没跑完，【app.session 本身是 undefined】。
     * 直接写 app.session.user 会抛
     *   TypeError: Cannot read properties of undefined (reading 'user')
     * 异常发生在 initializer 内部 → 整个扩展
     * "acme-auth-sms-wechat failed to initialize"，底部导航一个节点都渲染不出来。
     */
    const user = app.session && app.session.user;
    const debug = debugNav();

    const allTags = getTags();
    const myClass = myClassTag();

    // slug 为空/非法（改标签名时可能被清空）的次标签不显示，
    // 否则点进去是 /t/ 这种空地址，服务端 firstOrFail 会抛 500。
    const subs = myClass
      ? childTagsOf(myClass)
          .filter((sub) => validSlug(sub.slug))
          // 按后台排序位、再按 id 兜底，保证四个板块顺序稳定
          .sort((a, b) => {
            const pa = a.position;
            const pb = b.position;
            if (pa !== null && pb !== null && pa !== pb) return pa - pb;
            return String(a.id).localeCompare(String(b.id), 'en', { numeric: true });
          })
      : [];

    // 诊断信息：手机上没法开控制台，用 ?nav=debug 直接看；也能在 F12 里读
    const info = {
      logged: !!user,
      tags: allTags.length,
      cls: myClass ? `${myClass.id}/${myClass.name}/${myClass.slug}` : null,
      subs: subs.map((s) => `${s.id}/${s.name}/${s.slug}`),
      urls: subs.map((s) => tagUrl(s)),
    };

    try {
      window.__acmeNavDiag = info;
      window.__acmeTags = diag();
    } catch (e) {
      // ignore
    }

    if (!user) return debug ? this.debugBar(info, '未登录') : null;

    /*
     * 【1.8.7】未绑定班级的注册用户 → 不显示底部班级导航。
     *
     * 判定交给后端：Access\ClassAccess::canReadAnything() 算好写进 payload
     * （已镜像到 forums 资源，前端 canReadContent() 读得到）。
     * 不能再用 myClassTag() 推断 —— 它靠「能否在某主标签下发帖」判断班级，
     * 本站全员 canStartDiscussion=false，推断会退化成随便挑一个班，
     * 于是普通注册用户也挂着 AI-02 班的导航条。
     *
     * 返回 null 时只有容器 div 留着（.AcmeClassNav-root 本身没有样式），不占位。
     * canReadContent() 返回 null = 后端值还没读到，此时照常显示，绝不误隐藏。
     */
    if (canReadContent() === false) {
      return debug ? this.debugBar(info, '未绑定班级') : null;
    }

    if (!allTags.length) return debug ? this.debugBar(info, '标签数据未加载') : null;
    if (!myClass) return debug ? this.debugBar(info, '找不到所属班级') : null;
    if (!subs.length) return debug ? this.debugBar(info, '该班没有次标签') : null;

    // 次标签名若带班级前缀，底部导航只显示去掉前缀后的板块名，
    // 否则 4 个长名字在一行里会挤成一团。
    const clsName = myClass.name;

    return (
      <nav className="AcmeClassNav">
        {subs.map((sub) => {
          const url = tagUrl(sub);

          return (
            <a className="AcmeClassNav-link" href={url} onclick={(e) => this.go(e, url)}>
              {stripClassPrefix(sub.name, clsName)}
            </a>
          );
        })}
      </nav>
    );
  }

  // ?nav=debug 时渲染的诊断条
  debugBar(info, reason) {
    return (
      <nav className="AcmeClassNav AcmeClassNav--debug">
        <div className="AcmeClassNav-link">导航未显示：{reason}</div>
        <div className="AcmeClassNav-link">标签数 {info.tags} / 班级 {info.cls || '无'}</div>
      </nav>
    );
  }
}
